import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import './Settings.css';
import axiosInstance from "../api";
import TeamManagement from './TeamManagement';
import TwoFactorAuth from './TwoFactorAuth';
import ChangePassword from './ChangePassword';
import SsoSettings from './SsoSettings';

// Test-execution schedule options. Starter pack: minimum cadence is once every 24 hours
// (the backend also rejects anything more frequent than daily). Values are the natural-language
// intervals the backend CronExpressionConverter understands.
const SCHEDULE_OPTIONS = [
  { value: '1 day',   labelKey: 'Settings.freqDaily',       fallback: 'Daily' },
  { value: '2 days',  labelKey: 'Settings.freqEvery2Days',  fallback: 'Every 2 days' },
  { value: '3 days',  labelKey: 'Settings.freqEvery3Days',  fallback: 'Every 3 days' },
  { value: '1 week',  labelKey: 'Settings.freqWeekly',      fallback: 'Weekly' },
  { value: '1 month', labelKey: 'Settings.freqMonthly',     fallback: 'Monthly' },
];

const DOW_NAME = { '0': 'Sun', '1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun',
  SUN: 'Sun', MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat' };
const isDowSet = (dow) => dow && dow !== '*' && dow !== '?';

// Render a stored 6-field Spring cron as a friendly cadence (no raw cron shown to users).
function cronToHuman(cron) {
  if (!cron) return '';
  const f = cron.trim().split(/\s+/);
  if (f.length < 6) return cron;
  const dom = f[3], mon = f[4], dow = f[5];
  if (isDowSet(dow)) { const d = DOW_NAME[dow.toUpperCase()]; return d ? `Weekly (${d})` : 'Weekly'; }
  if (dom === '1') {
    if (mon && mon.startsWith('*/')) { const n = mon.slice(2); return n === '1' ? 'Monthly' : `Every ${n} months`; }
    return 'Monthly';
  }
  if (dom && dom.startsWith('*/')) { const n = dom.slice(2); return n === '1' ? 'Daily' : `Every ${n} days`; }
  return 'Daily';
}

// Map a stored cron back to the matching SCHEDULE_OPTIONS value (for the edit dropdown).
function cronToScheduleValue(cron) {
  const f = (cron || '').trim().split(/\s+/);
  if (f.length < 6) return '1 day';
  const dom = f[3], mon = f[4], dow = f[5];
  if (isDowSet(dow)) return '1 week';
  if (dom === '1') { const n = (mon && mon.startsWith('*/')) ? mon.slice(2) : '1'; return `${n} month${n === '1' ? '' : 's'}`; }
  if (dom && dom.startsWith('*/')) { const n = dom.slice(2); return `${n} day${n === '1' ? '' : 's'}`; }
  return '1 day';
}

// Render the raw ISO/microsecond timestamp as a readable local date-time.
function formatLastExecution(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Settings() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSetting, setNewSetting] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [organizationSettings, setOrganizationSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(location.state?.tab || 'organization');

  // Caller role — used to decide if org selector is needed
  const callerRole    = localStorage.getItem('role')    || '';
  const callerOrgRole = localStorage.getItem('orgRole') || '';
  const needsOrgPicker = callerRole === 'ROLE_PLATFORM_ADMIN'
    || callerOrgRole === 'CISO_RESELLER'
    || callerOrgRole === 'CPA_ADMIN'
    || callerOrgRole === 'CPA_AUDITOR';
  // Only an org admin may change the test-execution schedule (backend enforces this too).
  const isOrgAdmin = callerOrgRole === 'ORG_ADMIN'
    || callerRole === 'ROLE_ADMIN'
    || callerRole === 'ROLE_PLATFORM_ADMIN';

  // Jira Forge integration state
  const [jiraWebhookUrl,    setJiraWebhookUrl]    = useState('');
  const [jiraSecret,        setJiraSecret]        = useState('');
  const [jiraHasSecret,     setJiraHasSecret]     = useState(false);
  const [jiraSaving,        setJiraSaving]        = useState(false);
  const [jiraMsg,           setJiraMsg]           = useState('');

  // API Keys state
  const [apiKeys, setApiKeys]               = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [showNewKeyForm, setShowNewKeyForm] = useState(false);
  const [newKeyName, setNewKeyName]         = useState('');
  const [generatingKey, setGeneratingKey]   = useState(false);
  const [newKeyRaw, setNewKeyRaw]           = useState(null);
  const [revokingId, setRevokingId]         = useState(null);
  const [apiKeyMsg, setApiKeyMsg]           = useState('');
  const [keysCopied, setKeysCopied]         = useState(false);

  // Org picker state (only for admin / reseller)
  const [orgList, setOrgList]               = useState([]);
  const [orgListLoading, setOrgListLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId]   = useState('');

  // Org profile state
  const [orgProfile, setOrgProfile]         = useState(null);
  const [profileForm, setProfileForm]       = useState({ websiteUrl: '', contactEmail: '', address: '' });
  const [profileSaving, setProfileSaving]   = useState(false);
  const [profileMsg, setProfileMsg]         = useState('');
  const [logoFile, setLogoFile]             = useState(null);
  const [logoUploading, setLogoUploading]   = useState(false);
  const logoFileRef = useRef(null);

  // Demo Mode state
  const [demoStatus, setDemoStatus]     = useState(null);
  const [demoLoading, setDemoLoading]   = useState(false);
  const [demoMsg, setDemoMsg]           = useState('');

  const fetchOrganizationSettings = async () => {
    const authToken = localStorage.getItem('authToken');
    setLoading(true);

    try {
      const response = await axiosInstance.get('/organization-settings', {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (response.status === 200 && response.data) {
        // Convert single object to array if needed
        const settingsArray = Array.isArray(response.data) 
          ? response.data 
          : [response.data];
        setOrganizationSettings(settingsArray);
        setErrorMessage('');
      } else {
        setOrganizationSettings([]);
        setErrorMessage(t('Settings.errNoData'));
      }
    } catch (error) {
      setOrganizationSettings([]);
      setErrorMessage(t('Settings.errFetchSettings'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgProfile = async () => {
    try {
      const res = await axiosInstance.get('/org-profile');
      if (res.data) {
        setOrgProfile(res.data);
        setProfileForm({
          websiteUrl: res.data.orgWebsiteUrl || '',
          contactEmail: res.data.contactEmail || '',
          address: res.data.address || '',
        });
      }
    } catch {
      /* profile fetch is best-effort */
    }
  };

  const handleProfileSave = async () => {
    setProfileSaving(true);
    setProfileMsg('');
    try {
      const res = await axiosInstance.put('/org-profile', profileForm);
      setOrgProfile(res.data);
      setProfileMsg(t('Settings.profileSaved'));
    } catch {
      setProfileMsg(t('Settings.profileSaveFailed'));
    } finally {
      setProfileSaving(false);
    }
  };

  // Upload immediately when a file is chosen (pass the file directly — setLogoFile is async).
  // Choosing an image and clicking the profile "Save" never uploaded the logo, because Save
  // hits PUT /org-profile (no logo) — the logo has its own POST /org-profile/logo endpoint.
  const handleLogoUpload = async (fileArg) => {
    const file = fileArg || logoFile;
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setLogoUploading(true);
    setProfileMsg('');
    try {
      const res = await axiosInstance.post('/org-profile/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setOrgProfile(prev => ({ ...prev, logoUrl: res.data.logoUrl }));
      setLogoFile(null);
      setProfileMsg(t('Settings.logoUploaded'));
    } catch {
      setProfileMsg(t('Settings.logoUploadFailed'));
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0] || null;
    setLogoFile(file);
    if (file) handleLogoUpload(file); // auto-save on choose
  };

  const fetchJiraForgeConfig = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/integrations/jira-forge');
      if (res.data.webhookUrl) setJiraWebhookUrl(res.data.webhookUrl);
      setJiraHasSecret(!!res.data.hasSecret);
    } catch { /* best-effort */ }
  }, []);

  const saveJiraForgeConfig = async () => {
    setJiraSaving(true);
    setJiraMsg('');
    try {
      const body = { webhookUrl: jiraWebhookUrl };
      if (jiraSecret) body.webhookSecret = jiraSecret;
      const res = await axiosInstance.put('/integrations/jira-forge', body);
      setJiraHasSecret(!!res.data.hasSecret);
      setJiraSecret('');
      setJiraMsg(t('Settings.jiraSaved'));
    } catch {
      setJiraMsg(t('Settings.jiraSaveFailed'));
    } finally {
      setJiraSaving(false);
    }
  };

  const orgIdParam = needsOrgPicker && selectedOrgId ? { orgId: selectedOrgId } : {};

  const handleSignOutAllDevices = async () => {
    if (!window.confirm(t('Settings.confirmSignOutAll'))) return;
    try {
      await axiosInstance.post('/auth/sign-out-all');
    } catch { /* best-effort */ } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('username');
      localStorage.removeItem('orgRole');
      localStorage.removeItem('role');
      localStorage.removeItem('userName');
      localStorage.removeItem('organization');
      window.location.href = '/#/login';
    }
  };

  const fetchApiKeys = useCallback(async () => {
    if (needsOrgPicker && !selectedOrgId) return; // wait for org selection
    setApiKeysLoading(true);
    const params = needsOrgPicker && selectedOrgId ? { orgId: selectedOrgId } : {};
    try {
      const res = await axiosInstance.get('/integrations/api-keys', { params });
      setApiKeys(res.data || []);
    } catch {
      setApiKeyMsg(t('Settings.apiKeysLoadFailed'));
    } finally {
      setApiKeysLoading(false);
    }
  }, [needsOrgPicker, selectedOrgId, t]);

  const generateApiKey = async () => {
    if (needsOrgPicker && !selectedOrgId) { setApiKeyMsg(t('Settings.selectOrgFirst')); return; }
    if (!newKeyName.trim()) { setApiKeyMsg(t('Settings.enterKeyName')); return; }
    setGeneratingKey(true);
    setApiKeyMsg('');
    try {
      const res = await axiosInstance.post('/integrations/api-keys', { name: newKeyName.trim() }, { params: orgIdParam });
      setNewKeyRaw(res.data);
      setNewKeyName('');
      setShowNewKeyForm(false);
      fetchApiKeys();
    } catch {
      setApiKeyMsg(t('Settings.apiKeyGenerateFailed'));
    } finally {
      setGeneratingKey(false);
    }
  };

  const revokeApiKey = async (id) => {
    if (!window.confirm(t('Settings.confirmRevokeKey'))) return;
    setRevokingId(id);
    setApiKeyMsg('');
    try {
      await axiosInstance.delete(`/integrations/api-keys/${id}`, { params: orgIdParam });
      setApiKeys(prev => prev.map(k => k.id === id ? { ...k, active: false } : k));
    } catch {
      setApiKeyMsg(t('Settings.apiKeyRevokeFailed'));
    } finally {
      setRevokingId(null);
    }
  };

  const copyRawKey = () => {
    if (newKeyRaw?.rawKey) {
      navigator.clipboard.writeText(newKeyRaw.rawKey);
      setKeysCopied(true);
      setTimeout(() => setKeysCopied(false), 2000);
    }
  };

  useEffect(() => {
    fetchOrganizationSettings();
    fetchOrgProfile();
    // Load demo status on mount so the Integrations tab can show a guard immediately
    axiosInstance.get('/demo/status')
      .then(r => setDemoStatus(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTab !== 'integrations') return;
    if (needsOrgPicker && orgList.length === 0) {
      setOrgListLoading(true);
      axiosInstance.get('/iasm/admin/orgs')
        .then(r => {
          setOrgList(r.data || []);
          if (!selectedOrgId && r.data?.length > 0) setSelectedOrgId(String(r.data[0].id));
        })
        .catch(() => {})
        .finally(() => setOrgListLoading(false));
    }
    if (!needsOrgPicker) fetchApiKeys();
    fetchJiraForgeConfig();
  }, [selectedTab, needsOrgPicker, orgList.length, selectedOrgId, fetchApiKeys, fetchJiraForgeConfig]);

  useEffect(() => {
    if (selectedTab === 'integrations' && selectedOrgId) fetchApiKeys();
  }, [selectedOrgId, fetchApiKeys, selectedTab]);

  const handleAddSetting = async () => {
    if (!newSetting) {
      setErrorMessage(t('Settings.provideValidSetting'));
      return;
    }

    const authToken = localStorage.getItem('authToken');

    try {
      const requestBody = { schedulingCronExpression: newSetting };
      const response = await axiosInstance.post('/organization-settings/add', requestBody, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (response.status === 201) {
        setNewSetting('');
        setIsModalOpen(false);
        fetchOrganizationSettings();
      }
    } catch (error) {
      setErrorMessage(t('Settings.errAddSetting'));
    }
  };
  
  const renderProfileTab = () => (
    <div className="settings-section">
      <div className="settings-header">
        <h2>{t('Settings.orgProfileTitle')}</h2>
      </div>

      {/* Logo */}
      <div className="settings-group">
        <h3 style={{ textAlign: 'left' }}>{t('Settings.logo')}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{
            width: 80, height: 80, borderRadius: 10,
            background: '#f1f5f9', border: '1px solid #e2e8f0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden',
          }}>
            {orgProfile?.logoUrl
              ? <img src={orgProfile.logoUrl} alt={t('Settings.logo')} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : <i className="fas fa-building" style={{ fontSize: 28, color: '#94a3b8' }} />}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              ref={logoFileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleLogoFileChange}
            />
            <button className="secondary-btn" onClick={() => logoFileRef.current?.click()} disabled={logoUploading}>
              {logoUploading
                ? <>{t('Settings.uploading')}</>
                : <><i className="fas fa-image" /> {orgProfile?.logoUrl ? t('Settings.replaceImage') : t('Settings.chooseImage')}</>}
            </button>
            <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>
              {t('Settings.logoHint')}
            </p>
          </div>
        </div>
      </div>

      {/* Website + Contact */}
      <div className="settings-group">
        <h3 style={{ textAlign: 'left' }}>{t('Settings.contactInfo')}</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 480 }}>
          <div className="form-group">
            <label>{t('Settings.websiteUrl')}</label>
            <input
              type="url"
              placeholder="https://yourcompany.com"
              value={profileForm.websiteUrl}
              onChange={e => setProfileForm({ ...profileForm, websiteUrl: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>{t('Settings.contactEmail')}</label>
            <input
              type="email"
              placeholder="security@yourcompany.com"
              value={profileForm.contactEmail}
              onChange={e => setProfileForm({ ...profileForm, contactEmail: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>{t('Settings.address')}</label>
            <input
              type="text"
              placeholder={t('Settings.addressPlaceholder')}
              value={profileForm.address}
              onChange={e => setProfileForm({ ...profileForm, address: e.target.value })}
            />
          </div>
          {profileMsg && (
            <div style={{
              padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.8125rem',
              background: profileMsg.includes('success') ? '#d1fae5' : '#fee2e2',
              color: profileMsg.includes('success') ? '#065f46' : '#dc2626',
            }}>
              {profileMsg}
            </div>
          )}
          <button className="primary-btn" onClick={handleProfileSave} disabled={profileSaving}
            style={{ width: 'fit-content' }}>
            {profileSaving ? t('Settings.saving') : <><i className="fas fa-save" /> {t('Settings.saveProfile')}</>}
          </button>
        </div>
      </div>
    </div>
  );

  const renderOrganizationTab = () => (
    <div className="settings-section">
      <div className="settings-header">
        <h2>{t('Settings.orgSettingsTitle')}</h2>
      </div>

      {isModalOpen ? (
        <div className="inline-form">
          <div className="form-group">
            <label htmlFor="cronExpression">{t('Settings.scheduleFrequency')}</label>
            <select
              id="cronExpression"
              value={newSetting || '1 day'}
              onChange={(e) => setNewSetting(e.target.value)}
            >
              {SCHEDULE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>
              ))}
            </select>
            <small style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
              {t('Settings.scheduleHint')}
            </small>
          </div>
          {errorMessage && <div className="error-message">{errorMessage}</div>}
          <div className="form-actions">
            <button className="secondary-btn" onClick={() => setIsModalOpen(false)}>
              {t('Settings.cancel')}
            </button>
            <button className="primary-btn" onClick={handleAddSetting}>
              {t('Settings.saveSchedule', 'Save')}
            </button>
          </div>
        </div>
      ) : (
        <>
          {isOrgAdmin && organizationSettings.filter(s => s.schedulingCronExpression).length === 0 && (
            <div className="add-setting-btn-wrapper">
              <button className="add-setting-btn" onClick={() => { setNewSetting('1 day'); setErrorMessage(''); setIsModalOpen(true); }}>
                <i className="fas fa-plus"></i>
                {t('Settings.addSetting')}
              </button>
            </div>
          )}

          {loading ? (
            <div className="loading-spinner">{t('Settings.loading')}</div>
          ) : errorMessage ? (
            <div className="error-message">{errorMessage}</div>
          ) : organizationSettings && organizationSettings.filter(s => s.schedulingCronExpression).length > 0 ? (
            <div className="settings-table">
              <div className="table-header">
                <div className="th">{t('Settings.thOrgName')}</div>
                <div className="th">{t('Settings.thSchedule', 'Schedule')}</div>
                <div className="th">{t('Settings.thLastExecution')}</div>
                {isOrgAdmin && (
                  <div className="th">{t('Settings.thActions')}</div>
                )}
              </div>
              {organizationSettings.filter(s => s.schedulingCronExpression).map((setting, index) => (
                <div key={index} className="table-row">
                  <div className="td">{setting.organizationName}</div>
                  <div className="td" title={setting.schedulingCronExpression}>
                    {cronToHuman(setting.schedulingCronExpression)}
                  </div>
                  <div className="td">{formatLastExecution(setting.lastExecution)}</div>
                  {isOrgAdmin && (
                  <div className="td actions">
                    <button
                      className="icon-btn"
                      title={t('Settings.edit')}
                      onClick={() => { setNewSetting(cronToScheduleValue(setting.schedulingCronExpression)); setErrorMessage(''); setIsModalOpen(true); }}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                  </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <i className="fas fa-cog empty-icon"></i>
              <p>{t('Settings.noSettings')}</p>
              {isOrgAdmin && (
                <button className="primary-btn" onClick={() => { setNewSetting('1 day'); setErrorMessage(''); setIsModalOpen(true); }}>
                  {t('Settings.addFirstSetting')}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
  

  const renderSecurityTab = () => (
    <div className="settings-section">
      <div className="settings-header">
        <h2>{t('Settings.securitySettingsTitle')}</h2>
      </div>
      <div className="settings-content-area">
        <TwoFactorAuth />
        <ChangePassword />
        <div className="settings-group">
          <h3 style={{ textAlign: 'left' }}>{t('Settings.sessionManagement')}</h3>
          <div className="setting-item">
            <div className="setting-control">
              <button className="secondary-btn" onClick={handleSignOutAllDevices}>
                <i className="fas fa-sign-out-alt"></i>
                {t('Settings.signOutAllDevices')}
              </button>
            </div>
            <div className="setting-info" style={{ textAlign: 'left' }}>
              <h4>{t('Settings.signOutAllDevices')}</h4>
              <p>{t('Settings.signOutAllDevicesDesc')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="settings-section">
      <div className="settings-header">
        <h2>{t('Settings.notificationPrefsTitle')}</h2>
      </div>
      <div className="settings-content-area">
        <div className="settings-group">
          <h3>{t('Settings.emailNotifications')}</h3>
          <div className="setting-item">
            <div className="setting-control">
              <label className="switch" title={t('Settings.notificationPrefComingSoon')}>
                <input type="checkbox" checked disabled />
                <span className="slider disabled"></span>
              </label>
            </div>
            <div className="setting-info">
              <h4>{t('Settings.securityAlerts')}</h4>
              <p>{t('Settings.securityAlertsDesc')}</p>
            </div>
          </div>
          <div className="setting-item">
            <div className="setting-control">
              <label className="switch" title={t('Settings.notificationPrefComingSoon')}>
                <input type="checkbox" checked disabled />
                <span className="slider disabled"></span>
              </label>
            </div>
            <div className="setting-info">
              <h4>{t('Settings.scanResults')}</h4>
              <p>{t('Settings.scanResultsDesc')}</p>
            </div>
          </div>
          <p className="setting-note">{t('Settings.notificationPrefComingSoon')}</p>
        </div>
      </div>
    </div>
  );

  const renderIntegrationsTab = () => (
    <div className="settings-section">
      <div className="settings-header">
        <h2>{t('Settings.connectedServices')}</h2>
      </div>
      <div className="settings-content-area">

        {/* Demo mode guard banner */}
        {demoStatus?.isDemo && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 12,
            background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10,
            padding: '14px 16px', marginBottom: 20,
          }}>
            <i className="fas fa-flask" style={{ color: '#d97706', fontSize: 18, marginTop: 1 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#78350f', marginBottom: 4 }}>{t('Settings.demoActiveBannerTitle')}</div>
              <div style={{ fontSize: 13, color: '#92400e' }}>
                {t('Settings.demoActiveBannerBody1')}
                <strong>{t('Settings.demoTabName')}</strong>{t('Settings.demoActiveBannerBody2')}<strong>{t('Settings.resetDemoName')}</strong>{t('Settings.demoActiveBannerBody3')}
              </div>
            </div>
          </div>
        )}

        {/* Cloud integrations */}
        <div className="settings-group">
          <div className="integration-grid">
            <div className="integration-card">
              <div className="integration-header">
                <i className="fab fa-aws"></i>
                <h4>{t('Settings.awsTitle')}</h4>
              </div>
              <p>{t('Settings.awsDesc')}</p>
              <button className="primary-btn" disabled={demoStatus?.isDemo}
                      title={demoStatus?.isDemo ? t('Settings.exitDemoFirst') : ''}>
                <i className="fas fa-plug"></i>
                {t('Settings.connect')}
              </button>
            </div>
            <div className="integration-card">
              <div className="integration-header">
                <i className="fab fa-microsoft"></i>
                <h4>{t('Settings.azureTitle')}</h4>
              </div>
              <p>{t('Settings.azureDesc')}</p>
              <button className="primary-btn" disabled={demoStatus?.isDemo}
                      title={demoStatus?.isDemo ? t('Settings.exitDemoFirst') : ''}>
                <i className="fas fa-plug"></i>
                {t('Settings.connect')}
              </button>
            </div>
          </div>
        </div>

        {/* Jira Forge Integration */}
        <div className="settings-group">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <i className="fab fa-atlassian" style={{ fontSize: '1.5rem', color: '#0052cc' }}></i>
            <h3 style={{ margin: 0, textAlign: 'left' }}>{t('Settings.jiraForgeTitle')}</h3>
            {jiraWebhookUrl && <span className="key-status-badge active">{t('Settings.configured')}</span>}
          </div>
          <p style={{ fontSize: '0.875rem', color: '#64748b', margin: '0 0 1rem 0', textAlign: 'left' }}>
            {t('Settings.jiraForgeDesc')}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 520 }}>
            <div className="form-group">
              <label>{t('Settings.forgeWebtriggerUrl')}</label>
              <input
                type="url"
                value={jiraWebhookUrl}
                onChange={e => setJiraWebhookUrl(e.target.value)}
                placeholder="https://webhook.atlassian.com/x1/…"
              />
              <small style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: 4, display: 'block' }}>
                {t('Settings.forgeWebtriggerHint1')}<code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>forge webtrigger --environment production</code>{t('Settings.forgeWebtriggerHint2')}
              </small>
            </div>

            <div className="form-group">
              <label>{t('Settings.sharedSecret')}</label>
              <input
                type="password"
                value={jiraSecret}
                onChange={e => setJiraSecret(e.target.value)}
                placeholder={jiraHasSecret ? t('Settings.secretSavedPlaceholder') : t('Settings.secretPastePlaceholder')}
              />
              <small style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: 4, display: 'block' }}>
                {jiraHasSecret
                  ? <span style={{ color: '#16a34a' }}>{t('Settings.secretSavedHint')}</span>
                  : t('Settings.secretGenerateHint')}
              </small>
            </div>

            {jiraMsg && (
              <div style={{
                padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.8125rem',
                background: jiraMsg.includes('Failed') ? '#fee2e2' : '#d1fae5',
                color:      jiraMsg.includes('Failed') ? '#dc2626'  : '#065f46',
              }}>
                {jiraMsg}
              </div>
            )}

            <button className="primary-btn" onClick={saveJiraForgeConfig} disabled={jiraSaving} style={{ width: 'fit-content' }}>
              {jiraSaving ? t('Settings.saving') : <><i className="fas fa-save"></i> {t('Settings.saveJiraSettings')}</>}
            </button>
          </div>
        </div>

        {/* API Keys */}
        <div className="settings-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, textAlign: 'left' }}>{t('Settings.apiKeysTitle')}</h3>
            {!showNewKeyForm && (
              <button
                className="primary-btn"
                onClick={() => { setShowNewKeyForm(true); setApiKeyMsg(''); setNewKeyRaw(null); }}
                disabled={needsOrgPicker && !selectedOrgId}
                title={needsOrgPicker && !selectedOrgId ? t('Settings.selectOrgFirstTitle') : undefined}
              >
                <i className="fas fa-plus"></i>
                {t('Settings.generateKey')}
              </button>
            )}
          </div>

          {/* Org selector — shown only for platform admin / reseller */}
          {needsOrgPicker && (
            <div className="form-group" style={{ maxWidth: 360, marginBottom: '1.25rem' }}>
              <label htmlFor="keyOrgSelect">
                <i className="fas fa-building" style={{ marginRight: 6, color: '#3b82f6' }}></i>
                {t('Settings.organisation')}
              </label>
              {orgListLoading ? (
                <p style={{ fontSize: '0.875rem', color: '#64748b' }}>{t('Settings.loadingOrgs')}</p>
              ) : (
                <select
                  id="keyOrgSelect"
                  value={selectedOrgId}
                  onChange={e => { setSelectedOrgId(e.target.value); setApiKeys([]); setNewKeyRaw(null); setApiKeyMsg(''); }}
                  style={{ width: '100%', padding: '0.625rem', border: '1px solid #e2e8f0', borderRadius: '0.375rem', fontSize: '0.875rem', background: 'white' }}
                >
                  <option value="">{t('Settings.selectOrgOption')}</option>
                  {orgList.map(o => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                </select>
              )}
              {!selectedOrgId && !orgListLoading && (
                <p style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: 4 }}>
                  {t('Settings.selectOrgHint')}
                </p>
              )}
            </div>
          )}

          <p style={{ fontSize: '0.875rem', color: '#64748b', margin: '0 0 1rem 0', textAlign: 'left' }}>
            {t('Settings.apiKeysDesc')}
          </p>

          {apiKeyMsg && (
            <div style={{
              padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.8125rem', marginBottom: '1rem',
              background: apiKeyMsg.includes('Failed') ? '#fee2e2' : '#d1fae5',
              color:      apiKeyMsg.includes('Failed') ? '#dc2626'  : '#065f46',
            }}>
              {apiKeyMsg}
            </div>
          )}

          {/* New raw key display — shown once after generation */}
          {newKeyRaw && (
            <div className="api-key-reveal">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <i className="fas fa-check-circle" style={{ color: '#16a34a' }}></i>
                <strong style={{ fontSize: '0.875rem', color: '#15803d' }}>
                  {t('Settings.keyGenerated', { name: newKeyRaw.name })}
                </strong>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <code className="api-key-display">{newKeyRaw.rawKey}</code>
                <button className="secondary-btn" onClick={copyRawKey} style={{ whiteSpace: 'nowrap' }}>
                  <i className={`fas fa-${keysCopied ? 'check' : 'copy'}`}></i>
                  {keysCopied ? t('Settings.copied') : t('Settings.copy')}
                </button>
                <button className="secondary-btn" onClick={() => setNewKeyRaw(null)} title={t('Settings.dismiss')}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          )}

          {/* Inline new-key form */}
          {showNewKeyForm && (
            <div className="inline-form" style={{ marginBottom: '1rem' }}>
              <div className="form-group">
                <label htmlFor="newKeyName">{t('Settings.keyName')}</label>
                <input
                  id="newKeyName"
                  type="text"
                  value={newKeyName}
                  onChange={e => setNewKeyName(e.target.value)}
                  placeholder={t('Settings.keyNamePlaceholder')}
                  onKeyDown={e => e.key === 'Enter' && generateApiKey()}
                />
              </div>
              <div className="form-actions">
                <button className="secondary-btn" onClick={() => { setShowNewKeyForm(false); setNewKeyName(''); setApiKeyMsg(''); }}>
                  {t('Settings.cancel')}
                </button>
                <button className="primary-btn" onClick={generateApiKey} disabled={generatingKey}>
                  {generatingKey ? t('Settings.generating') : <><i className="fas fa-key"></i> {t('Settings.generate')}</>}
                </button>
              </div>
            </div>
          )}

          {/* Keys table */}
          {needsOrgPicker && !selectedOrgId ? null : apiKeysLoading ? (
            <div className="loading-spinner">{t('Settings.loading')}</div>
          ) : apiKeys.length === 0 ? (
            <div className="empty-state" style={{ padding: '2rem 1rem' }}>
              <i className="fas fa-key empty-icon"></i>
              <p>{t('Settings.noApiKeys')}</p>
            </div>
          ) : (
            <div className="settings-table">
              <div className="table-header" style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1.5fr 1fr 0.75fr' }}>
                <div className="th">{t('Settings.thName')}</div>
                <div className="th">{t('Settings.thPrefix')}</div>
                <div className="th">{t('Settings.thStatus')}</div>
                <div className="th">{t('Settings.thCreated')}</div>
                <div className="th">{t('Settings.thLastUsed')}</div>
                <div className="th">{t('Settings.thActions')}</div>
              </div>
              {apiKeys.map(key => (
                <div key={key.id} className="table-row" style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1.5fr 1fr 0.75fr' }}>
                  <div className="td">{key.name}</div>
                  <div className="td"><code>{key.keyPrefix}</code></div>
                  <div className="td">
                    <span className={`key-status-badge ${key.active ? 'active' : 'revoked'}`}>
                      {key.active ? t('Settings.statusActive') : t('Settings.statusRevoked')}
                    </span>
                  </div>
                  <div className="td" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {key.createdAt ? new Date(key.createdAt).toLocaleDateString() : '—'}
                  </div>
                  <div className="td" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : t('Settings.never')}
                  </div>
                  <div className="td actions">
                    {key.active && (
                      <button
                        className="icon-btn danger"
                        title={t('Settings.revokeKey')}
                        onClick={() => revokeApiKey(key.id)}
                        disabled={revokingId === key.id}
                      >
                        <i className="fas fa-ban"></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );

  const loadDemoStatus = async () => {
    try {
      const res = await axiosInstance.get('/demo/status');
      setDemoStatus(res.data);
    } catch {
      setDemoStatus(null);
    }
  };

  const handleSeedDemo = async () => {
    setDemoLoading(true);
    setDemoMsg('');
    try {
      const res = await axiosInstance.post('/demo/seed');
      setDemoMsg(res.data?.message || t('Settings.demoDataLoaded'));
      setDemoStatus(prev => ({ ...prev, isDemo: true }));
      window.dispatchEvent(new CustomEvent('demoStatusChanged', { detail: { isDemo: true } }));
    } catch (e) {
      // 409 = blocked because real integrations exist; surface the server message directly
      const msg = e.response?.data?.message || e.message;
      setDemoMsg(msg);
      // Refresh status so the guard banner appears if hasRealIntegrations changed
      loadDemoStatus();
    } finally {
      setDemoLoading(false);
    }
  };

  const handleResetDemo = async () => {
    setDemoLoading(true);
    setDemoMsg('');
    try {
      const res = await axiosInstance.post('/demo/reset');
      setDemoMsg(res.data?.message || t('Settings.demoReset'));
      setDemoStatus(prev => ({ ...prev, isDemo: false }));
      window.dispatchEvent(new CustomEvent('demoStatusChanged', { detail: { isDemo: false } }));
    } catch (e) {
      setDemoMsg(t('Settings.demoResetFailed', { error: e.response?.data?.message || e.message }));
    } finally {
      setDemoLoading(false);
    }
  };

  const renderDemoTab = () => {
    const hasRealIntegrations = demoStatus?.hasRealIntegrations;
    const seedBlocked = hasRealIntegrations && !demoStatus?.isDemo;

    return (
    <div className="settings-section">
      <div className="settings-header">
        <h2>{t('Settings.demoSandboxTitle')}</h2>
        <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '4px 0 0' }}>
          {t('Settings.demoSandboxDesc1')}<strong>{t('Settings.allUsers')}</strong>{t('Settings.demoSandboxDesc2')}
        </p>
      </div>
      <div className="settings-content-area">
        <div className="settings-group">
          {demoStatus && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 14px', borderRadius: 20, marginBottom: 16,
              background: demoStatus.isDemo ? '#dcfce7' : '#f3f4f6',
              color: demoStatus.isDemo ? '#16a34a' : '#6b7280',
              fontSize: '0.8rem', fontWeight: 600,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
              {demoStatus.isDemo ? t('Settings.demoModeActive') : t('Settings.demoModeOff')}
            </div>
          )}

          {/* Guard banner — shown when real integrations exist and demo is not active */}
          {seedBlocked && (
            <div style={{
              marginBottom: 16, padding: '12px 16px', borderRadius: 8,
              background: '#fef3c7', border: '1px solid #fcd34d',
              display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>⚠️</span>
              <div style={{ fontSize: '0.85rem', color: '#92400e' }}>
                <strong>{t('Settings.demoUnavailableTitle')}</strong>{t('Settings.demoUnavailableBody')}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              className="primary-btn"
              onClick={() => { loadDemoStatus(); handleSeedDemo(); }}
              disabled={demoLoading || seedBlocked}
              title={seedBlocked ? t('Settings.seedBlockedTitle') : ''}
              style={seedBlocked ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
            >
              {demoLoading ? t('Settings.working') : t('Settings.loadDemoData')}
            </button>
            <button
              className="secondary-btn"
              onClick={() => { loadDemoStatus(); handleResetDemo(); }}
              disabled={demoLoading}
              style={{ background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb' }}
            >
              {demoLoading ? t('Settings.working') : t('Settings.resetDemo')}
            </button>
          </div>
          {demoMsg && (
            <div style={{
              marginTop: 16, padding: '10px 14px', borderRadius: 8,
              background: demoMsg.toLowerCase().includes('fail') || demoMsg.toLowerCase().includes('blocked') ? '#fee2e2' : '#d1fae5',
              color: demoMsg.toLowerCase().includes('fail') || demoMsg.toLowerCase().includes('blocked') ? '#dc2626' : '#065f46',
              fontSize: '0.85rem',
            }}>
              {demoMsg}
            </div>
          )}
        </div>
      </div>
    </div>
    );
  };

  return (
    <div className="settings-container">
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">{t('Settings.pageTitle')}</h1>
        </div>
      </div>
      <div className="settings-content">
        <div className="settings-tabs">
          <button
            className={`tab-btn ${selectedTab === 'profile' ? 'active' : ''}`}
            onClick={() => setSelectedTab('profile')}
          >
            <i className="fas fa-id-card"></i>
            {t('Settings.tabProfile')}
          </button>
          <button
            className={`tab-btn ${selectedTab === 'team' ? 'active' : ''}`}
            onClick={() => setSelectedTab('team')}
          >
            <i className="fas fa-users"></i>
            {t('Settings.tabTeam')}
          </button>
          <button
            className={`tab-btn ${selectedTab === 'organization' ? 'active' : ''}`}
            onClick={() => {
              setSelectedTab('organization');
              setIsModalOpen(false);
              setNewSetting('');
              setErrorMessage('');
            }}
          >
            <i className="fas fa-building"></i>
            {t('Settings.tabScheduling')}
          </button>
          <button
            className={`tab-btn ${selectedTab === 'security' ? 'active' : ''}`}
            onClick={() => setSelectedTab('security')}
          >
            <i className="fas fa-shield-alt"></i>
            {t('Settings.tabSecurity')}
          </button>
          <button
            className={`tab-btn ${selectedTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setSelectedTab('notifications')}
          >
            <i className="fas fa-bell"></i>
            {t('Settings.tabNotifications')}
          </button>
          <button
            className={`tab-btn ${selectedTab === 'integrations' ? 'active' : ''}`}
            onClick={() => setSelectedTab('integrations')}
          >
            <i className="fas fa-plug"></i>
            {t('Settings.tabIntegrations')}
          </button>
          {(callerOrgRole === 'ORG_ADMIN' || callerRole === 'ROLE_ADMIN' || callerRole === 'ROLE_PLATFORM_ADMIN') && (
            <button
              className={`tab-btn ${selectedTab === 'sso' ? 'active' : ''}`}
              onClick={() => setSelectedTab('sso')}
            >
              <i className="fas fa-key"></i>
              {t('Settings.tabSso', 'SSO')}
            </button>
          )}
          <button
            className={`tab-btn ${selectedTab === 'demo' ? 'active' : ''}`}
            onClick={() => { setSelectedTab('demo'); loadDemoStatus(); }}
          >
            <i className="fas fa-film"></i>
            {t('Settings.tabDemoMode')}
          </button>
        </div>

        {selectedTab === 'profile' && renderProfileTab()}
        {selectedTab === 'team' && <div className="settings-section"><div className="settings-header"><h2>{t('Settings.teamAccessTitle')}</h2></div><TeamManagement /></div>}
        {selectedTab === 'organization' && renderOrganizationTab()}
        {selectedTab === 'security' && renderSecurityTab()}
        {selectedTab === 'notifications' && renderNotificationsTab()}
        {selectedTab === 'integrations' && renderIntegrationsTab()}
        {selectedTab === 'sso' && <SsoSettings />}
        {selectedTab === 'demo' && renderDemoTab()}

      </div>
    </div>
  );
}

export default Settings;
